import { RouteRecordRaw } from 'vue-router';

import { ROUTE_NAMES } from '@/constants';
import { RouteTab } from '@/models/routing';
import i18n, { loadTranslations } from '@/plugins/i18n';

import { toNonVueRoute } from './utils';

// route-level code splitting
const OrderLog = () => import('@/pages/Orders/Log/OrderLog.vue');
const OrdersReceivedContainer = () => import('@/pages/Orders/Received/OrdersReceivedContainer.vue');
const OrdersSent = () => import('@/pages/Orders/Sent/OrdersSent.vue');
const OrderReturnList = () => import('@/pages/Orders/Returns/OrderReturnList.vue');
const OrderReturnAddEdit = () => import('@/pages/Orders/Returns/OrderReturnAddEdit.vue');
const OrderReturnSummary = () => import('@/pages/Orders/Returns/OrderReturnSummary.vue');
const OrderSplit = () => import('@/pages/Orders/Split/OrderSplit.vue');
const OrderSummary = () => import('@/pages/Orders/Summary/OrderSummary.vue');
const OrdersView = () => import('@/pages/Orders/index.vue');
const OrderCreateView = () => import('@/pages/Orders/Manual/OrderCreate.vue');
const OrderCloneView = () => import('@/pages/Orders/Clone/OrderClone.vue');

// BME routes
const BMEOrdersWrapper = () => import('@/pages/Orders/BMEWrapper.vue');
const BMEOrdersDraftList = () => import('@/pages/Orders/BMEDrafts/BMEOrdersDraftList.vue');
const BMEOrderCreateView = () => import('@/pages/Orders/BMECreate/BMEOrderCreate.vue');
const BMEOrderEditView = () => import('@/pages/Orders/BMEEdit/BMEOrderEdit.vue');
const BMEOrdersView = () => import('@/pages/Orders/BMEOrders/BMEOrders.vue');
const BMEOrderSummary = () => import('@/pages/Orders/BMESummary/BMEOrderSummary.vue');

// Collaborative routes
const CollabOrdersReceived = () => import('@/pages/Orders/Collaborative/CollabOrdersReceived.vue');
const CollaborativeSent = () => import('@/pages/Orders/Collaborative/CollaborativeSent.vue');

// Internal Approvals
const InternalApprovals = () => import('@/pages/Orders/InternalApprovals/InternalApprovals.vue');

// Uploaded Invoices
const UploadedOrders = () => import('@/pages/Orders/Uploaded/UploadedOrders.vue');

async function beforeEnter() {
  await Promise.all([loadTranslations('orders'), loadTranslations('crm'), loadTranslations('seedToSale')]);
}

export function bmeOrdersTabRoutes(): Readonly<RouteTab[]> {
  return Object.freeze([
    {
      to: { name: ROUTE_NAMES.BRAND_OWNER_ORDERS },
      label: i18n.t('common.orders'),
    },
    {
      to: { name: ROUTE_NAMES.BRAND_OWNER_DRAFT_ORDERS },
      label: i18n.t('orders.drafts'),
    },
  ]);
}

export function buyerOrdersTabRoutes(): Readonly<RouteTab[]> {
  return Object.freeze([
    {
      to: { name: ROUTE_NAMES.INTERNAL_APPROVALS_LIST },
      label: i18n.t('common.internalApprovals'),
      visible: ({ company, userPermissions }) => company?.buyer && userPermissions?.canAccessBuyerInternalApprovals,
    },
    {
      to: { name: ROUTE_NAMES.ORDERS_SENT },
      badge: 'submittedOrders',
      label: i18n.t('orders.sent.title'),
      visible: ({ company, userPermissions }) => company?.buyer && userPermissions?.canManageOrdersSent,
    },
    {
      to: { name: ROUTE_NAMES.COLLABORATIVE_RECEIVED },
      badge: 'collaborativeOrders',
      badgeColor: 'red',
      label: i18n.t('orders.collaborativeOrders'),
      visible: ({ company }) => company?.buyer,
    },
    {
      to: { name: ROUTE_NAMES.UPLOADED },
      label: i18n.t('orders.uploaded.title'),
      visible: ({ company, userPermissions }) => company?.buyer && userPermissions?.canAccessBuyerOrderUploads,
    },
  ]);
}

export function sellerOrdersTabRoutes(): Readonly<RouteTab[]> {
  return Object.freeze([
    {
      to: { name: ROUTE_NAMES.ORDERS_RECEIVED },
      badge: 'submittedOrders',
      label: i18n.t('orders.received.title'),
      visible: ({ company, userPermissions }) => company?.seller && userPermissions?.canManageOrdersReceived,
    },
    {
      to: { name: ROUTE_NAMES.ORDERS_DRAFT },
      badge: 'draftOrders',
      label: i18n.t('orders.drafts'),
      visible: ({ company, userPermissions }) => company?.seller && userPermissions?.canManageOrdersReceived,
    },
    {
      to: { name: ROUTE_NAMES.COLLABORATIVE_SENT },
      badge: 'collaborativeOrders',
      label: i18n.t('orders.collaborativeOrders'),
      visible: ({ company }) => company?.seller,
    },
    {
      to: { name: ROUTE_NAMES.ORDER_RETURNS },
      badge: 'submittedReturns',
      label: i18n.t('orders.returns.title'),
      visible: ({ company, userPermissions }) => company?.seller && userPermissions?.canManageOrdersReceived,
    },
    {
      to: { name: ROUTE_NAMES.CUSTOMER_REQUESTS },
      badge: 'pendingRequests',
      label: i18n.t('orders.customerRequests'),
      visible: ({ company, userPermissions }) => company?.seller && userPermissions?.canManageOrdersReceived,
    },
  ]);
}

export function ordersTabRoutes(): Readonly<RouteTab[]> {
  return Object.freeze([...buyerOrdersTabRoutes(), ...sellerOrdersTabRoutes()]);
}

const ordersRoutes: RouteRecordRaw[] = [
  // Legacy routes
  toNonVueRoute({ path: ':uuid/delete-draft/', name: ROUTE_NAMES.DELETE_DRAFT_ORDER }),
  toNonVueRoute({ path: ':uuid/invoice/', name: ROUTE_NAMES.ORDER_INVOICE }),
  toNonVueRoute({ path: ':uuid/reorder/', name: ROUTE_NAMES.REORDER_ORDER }),
  toNonVueRoute({ path: ':uuid/view_order_log/', name: ROUTE_NAMES.ORDER_VIEW_LOG }),
  toNonVueRoute({ path: 'deliveries/order/:uuid/view_order_log/', name: ROUTE_NAMES.DELIVERY_ORDER_LOG }),
  toNonVueRoute({ path: ':uuid/invoice/send/', name: ROUTE_NAMES.ORDER_INVOICE_SEND_EMAIL }),
  toNonVueRoute({ path: ':uuid/edit/reps/', name: ROUTE_NAMES.EDIT_ORDER_SALES_REPS }),
  // NOTE: Avoid using this path/route name. It will likely be deprecated after the Order Summary is vueified
  toNonVueRoute({ path: ':uuid/review/', name: ROUTE_NAMES.REVIEW_ORDER }),
  toNonVueRoute({ path: ':uuid/payments/', name: ROUTE_NAMES.ORDER_PAYMENTS }),
  toNonVueRoute({ path: 'orders/:uuid/view/', name: ROUTE_NAMES.ORDER_SUMMARY }),
  toNonVueRoute({ path: 'orders/draft/', name: ROUTE_NAMES.ORDERS_DRAFT }),
  toNonVueRoute({ path: 'orders/print/', name: ROUTE_NAMES.ORDERS_PRINT }),
  toNonVueRoute({ path: 'orders/requests/', name: ROUTE_NAMES.CUSTOMER_REQUESTS }),
  toNonVueRoute({ path: 'orders/requests/:uuid/', name: ROUTE_NAMES.CUSTOMER_REQUEST_VIEW }),
  toNonVueRoute({ path: '/c/:company/:uuid/invoice/', name: ROUTE_NAMES.ORDER_INVOICE_WITH_SLUG }),
  toNonVueRoute({ path: '/c/:company/orders/:uuid/view/', name: ROUTE_NAMES.ORDER_SUMMARY_WITH_SLUG }),

  // Orders Seller
  {
    path: 'orders/',
    component: OrdersView,
    beforeEnter,
    meta: {
      tabs: ordersTabRoutes,
    },
    children: [
      // Received
      {
        path: ':uuid/log',
        name: ROUTE_NAMES.ORDER_LOG,
        component: OrderLog,
        props: (route) => {
          return { uuid: route.params.uuid };
        },
        meta: {
          title: () => i18n.t('orders.auditLog.title'),
          description: () => i18n.t('orders.auditLog.description'),
          permissions: [{ rules: [({ user }) => user.isSuperuser] }], // TODO: add rule for is_editable_by_staff
        },
      },
      {
        path: ':uuid/summary',
        // NOTE: Remove ORDER_SUMMARY_VUE when the old order summary is deprecated
        // and use ORDER_SUMMARY here
        name: ROUTE_NAMES.ORDER_SUMMARY_VUE,
        component: OrderSummary,
        beforeEnter,
        props: (route) => {
          return { uuid: route.params.uuid };
        },
        meta: {
          title: () => i18n.t('common.order_summary'),
          permissions: [
            {
              rules: [
                ({ userActiveCompany }) =>
                  !!userActiveCompany?.staffId &&
                  !!(
                    userActiveCompany.staffPermissions.canManageOrdersSent ||
                    userActiveCompany.staffPermissions.canManageOrdersReceived ||
                    userActiveCompany.staffPermissions.canManageCrm
                  ),
              ],
            },
          ],
        },
      },
      {
        path: 'received',
        name: ROUTE_NAMES.ORDERS_RECEIVED,
        component: OrdersReceivedContainer,
        meta: {
          title: () => i18n.t('orders.received.title'),
          description: () => i18n.t('orders.received.description'),
          permissions: [
            {
              rules: [
                'canManageOrdersReceived',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.seller),
              ],
            },
          ],
        },
      },

      // Collaborative
      {
        path: 'collaborative/sent',
        name: ROUTE_NAMES.COLLABORATIVE_SENT,
        component: CollaborativeSent,
        meta: {
          title: () => i18n.t('common.orders'),
          permissions: [
            {
              rules: [
                'canManageOrdersReceived',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.seller),
              ],
            },
          ],
        },
      },

      // Returns
      {
        path: 'returns',
        name: ROUTE_NAMES.ORDER_RETURNS,
        component: OrderReturnList,
        meta: {
          title: () => i18n.t('orders.returns.title'),
          description: () => i18n.t('orders.returns.description'),
          permissions: [
            {
              rules: ['canManageOrdersReceived', ({ userActiveCompany }) => Boolean(userActiveCompany?.seller)],
            },
          ],
        },
      },
      {
        path: 'returns/:uuid/view',
        name: ROUTE_NAMES.ORDER_RETURN_SUMMARY,
        component: OrderReturnSummary,
        props: (route) => {
          return { uuid: route.params.uuid };
        },
        meta: {
          title: () => i18n.t('orders.returns.title'),
          permissions: [
            {
              rules: ['canManageOrdersReceived', ({ userActiveCompany }) => Boolean(userActiveCompany?.seller)],
            },
          ],
        },
      },
      {
        path: 'returns/create',
        name: ROUTE_NAMES.ORDER_RETURN_CREATE,
        component: OrderReturnAddEdit,
        meta: {
          title: () => i18n.t('orders.returns.title'),
          permissions: [
            {
              rules: ['canManageOrdersReceived', ({ userActiveCompany }) => Boolean(userActiveCompany?.seller)],
            },
          ],
        },
      },
      {
        path: 'returns/:uuid/edit',
        name: ROUTE_NAMES.ORDER_RETURN_EDIT,
        component: OrderReturnAddEdit,
        props: (route) => {
          return { uuid: route.params.uuid };
        },
        meta: {
          title: () => i18n.t('orders.returns.title'),
          permissions: [
            {
              rules: [
                'canManageOrdersReceived',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.seller),
              ],
            },
          ],
        },
      },

      // Manual Order routes
      {
        path: 'create',
        name: ROUTE_NAMES.CREATE_ORDER,
        component: OrderCreateView,
        meta: {
          title: () => i18n.t('common.createOrder'),
          permissions: [
            {
              rules: ['canManageOrdersReceived'],
            },
          ],
        },
      },
      {
        path: ':uuid/edit',
        name: ROUTE_NAMES.EDIT_ORDER,
        component: OrderCreateView,
        props: (route) => {
          return { uuid: route.params.uuid };
        },
        meta: {
          title: () => i18n.t('orders.editOrder'),
          permissions: [
            {
              rules: [
                'canManageOrdersReceived',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.seller && userActiveCompany.staffId),
                // TODO: Add rule for is_editable_by_staff
              ],
            },
          ],
        },
      },
      {
        path: ':uuid/clone',
        name: ROUTE_NAMES.ORDER_CLONE,
        component: OrderCloneView,
        props: (route) => {
          return { uuid: route.params.uuid };
        },
        meta: {
          title: () => i18n.t('common.cloneOrder'),
          permissions: [
            {
              rules: [
                ({ userActiveCompany }) =>
                  !!userActiveCompany?.staffId &&
                  !!(
                    userActiveCompany.staffPermissions.canManageOrdersSent ||
                    userActiveCompany.staffPermissions.canManageOrdersReceived ||
                    userActiveCompany.staffPermissions.canManageCrm
                  ),
              ],
            },
          ],
        },
      },
    ],
  },

  // Split Order
  {
    path: 'orders/:uuid/split',
    name: ROUTE_NAMES.ORDER_SPLIT,
    component: OrderSplit,
    beforeEnter,
    props: (route) => {
      return { uuid: route.params.uuid };
    },
    meta: {
      title: () => i18n.t('orders.split.title'),
      description: () => i18n.t('orders.split.description'),
      permissions: [
        {
          rules: [
            'ret_pod__split_orders',
            'canManageOrdersReceived',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.seller),
          ],
        },
      ],
    },
  },

  // Brand Manager orders list; this is an api-driven page so the data is not stored in vuex
  {
    path: 'brand-owner/orders/',
    name: ROUTE_NAMES.BRAND_OWNER_ORDERS,
    component: BMEOrdersView,
    beforeEnter,
    meta: {
      title: () => i18n.t('common.orders'),
      description: () => i18n.t('orders.brand_owner.description'),
      tabs: bmeOrdersTabRoutes,
      permissions: [
        {
          rules: [
            ({ activeCompany, userActiveCompany }) =>
              Boolean(
                activeCompany?.isBrandOwner && activeCompany.hasBrandOwnerOrdersEnabled && userActiveCompany?.staffId,
              ),
          ],
        },
      ],
    },
  },

  // Brand Manager orders
  toNonVueRoute({
    path: 'brand-owner/orders/:uuid/invoice/',
    name: ROUTE_NAMES.BRAND_OWNER_PRINT_DRAFT_ORDER,
  }),
  {
    path: 'brand-owner/orders/',
    component: BMEOrdersWrapper,
    beforeEnter,
    meta: {
      tabs: bmeOrdersTabRoutes,
    },
    children: [
      {
        path: 'drafts',
        name: ROUTE_NAMES.BRAND_OWNER_DRAFT_ORDERS,
        component: BMEOrdersDraftList,
        meta: {
          title: () => i18n.t('orders.drafts'),
          description: () => i18n.t('orders.brand_owner.drafts.description'),
          permissions: [
            {
              rules: [
                ({ activeCompany, userActiveCompany }) =>
                  Boolean(
                    activeCompany?.isBrandOwner &&
                      activeCompany.hasBrandOwnerOrdersEnabled &&
                      userActiveCompany?.staffId,
                  ),
              ],
            },
          ],
        },
      },
      {
        path: 'create',
        name: ROUTE_NAMES.CREATE_BRAND_OWNER_ORDER,
        component: BMEOrderCreateView,
        meta: {
          title: () => i18n.t('common.createOrder'),
          permissions: [
            {
              rules: [
                ({ activeCompany, userActiveCompany }) =>
                  Boolean(
                    activeCompany?.isBrandOwner &&
                      activeCompany.hasBrandOwnerOrdersEnabled &&
                      userActiveCompany?.staffId,
                  ),
              ],
            },
          ],
        },
      },
      {
        path: ':uuid/edit',
        name: ROUTE_NAMES.BRAND_OWNER_EDIT_DRAFT_ORDER,
        component: BMEOrderEditView,
        props: (route) => {
          return { uuid: route.params.uuid };
        },
        meta: {
          title: () => i18n.t('orders.brand_owner.drafts.edit.title'),
          permissions: [
            {
              rules: [
                ({ activeCompany, userActiveCompany }) =>
                  Boolean(
                    activeCompany?.isBrandOwner &&
                      activeCompany.hasBrandOwnerOrdersEnabled &&
                      userActiveCompany?.staffId,
                  ),
              ],
            },
          ],
        },
      },
      {
        path: ':uuid/view',
        name: ROUTE_NAMES.BRAND_OWNER_ORDER_SUMMARY,
        component: BMEOrderSummary,
        props: (route) => {
          return { uuid: route.params.uuid };
        },
        meta: {
          title: () => i18n.t('common.order_summary'),
          permissions: [
            {
              rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)],
            },
          ],
        },
      },
    ],
  },

  // Orders Buyer
  {
    path: 'orders/internal-approvals/',
    name: ROUTE_NAMES.INTERNAL_APPROVALS_LIST,
    component: InternalApprovals,
    beforeEnter,
    meta: {
      tabs: buyerOrdersTabRoutes,
      title: () => i18n.t('common.orders'),
      description: () => i18n.t('orders.sent.description'),
      permissions: [
        {
          rules: [
            'canManageOrdersSent',
            'enableInternalApprovals',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.buyer && userActiveCompany.staffId),
          ],
        },
      ],
    },
  },
  {
    path: 'orders/sent',
    name: ROUTE_NAMES.ORDERS_SENT,
    component: OrdersSent,
    beforeEnter,
    meta: {
      tabs: buyerOrdersTabRoutes,
      title: () => i18n.t('common.orders'),
      description: () => i18n.t('orders.sent.description'),
      permissions: [
        {
          rules: [
            'canManageOrdersSent',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.buyer && userActiveCompany.staffId),
          ],
        },
      ],
    },
  },
  {
    path: 'orders/collaborative/received',
    name: ROUTE_NAMES.COLLABORATIVE_RECEIVED,
    component: CollabOrdersReceived,
    beforeEnter,
    meta: {
      tabs: buyerOrdersTabRoutes,
      title: () => i18n.t('common.orders'),
      description: () => i18n.t('orders.sent.description'),
      permissions: [
        {
          rules: [
            'canManageOrdersSent',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.buyer),
          ],
        },
      ],
    },
  },
  {
    path: 'orders/uploaded',
    name: ROUTE_NAMES.UPLOADED,
    component: UploadedOrders,
    beforeEnter,
    meta: {
      tabs: buyerOrdersTabRoutes,
      title: () => i18n.t('common.orders'),
      description: () => i18n.t('orders.sent.description'),
      permissions: [
        {
          rules: [
            'canAccessBuyerInternalApprovals',
            'canManageOrdersSent',
            'enableOrderUploads',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.buyer && userActiveCompany.staffId),
          ],
        },
      ],
    },
  },
];

export default ordersRoutes;
